<template>
  <div class="insights">
    <div class="insights__tabs">
      <div class="insights__headerRow">
        <div class="insights__headerRow__tabName">{{ insightsHeaderText }}</div>
        <div class="insights__headerRow__audienceExpansion">
          <v-switch
            v-model="audienceExpansion"
            color="primary"
            hide-details
            dense
            inset
            @change="onAudienceExpansionToggle"
            :disabled="viewOnly || !allowAudienceExpansionToggle"
          >
            <template v-slot:label>
              <span
                class="audience-expansion-label"
                :class="{ 'audience-expansion-label--active': audienceExpansion }"
              >
                Audience Expansion
              </span>
            </template>
          </v-switch>
        </div>
        <!-- <div class="insights__headerRow__moreIcon">
          <img class="icon" :src="moreIcon">
        </div> -->
      </div>
      <scroll-panel class="insights__tabular-area">
        <primary
          :primaryInsights="workbenchSlottedValue || undefined"
          :loadingInsights="loadingInsights"
          :loadingWorkbench="loadingWorkbench"
        ></primary>
      </scroll-panel>
      <div class="insights__tabs__insightButtonRow">
        <interactive-simple-button
          :disabled="disableUpdateInsights"
          @click.native="updateInsights"
          :label="buttonText"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import ScrollPanel from '@/components/common/scrollPanel'
  import TabsComponents from '@/components/common/tabs'
  import Primary from './primary'
  import Secondary from './secondary'
  import { QUERY_FOR_DATA } from '@/appLogic/workbench/common/store/actions'
  import { SET_AUDIENCE_EXPANSION, CONFIGURE_AUDIENCE_EXPANSION } from '@/appLogic/workbench/Xandr/store/actions'
  import { InteractiveSimpleButton } from '@/components/common/simpleButton'
  import MoreIcons from '@/assets/Xandr/newMoreIcon-sm.png'
  import { ACTIVATION_STATUS, DESTINATIONS } from '@/appLogic/targets/Constants'
  import { mapGetters } from 'vuex'

  const { Tabs, Tab } = TabsComponents

  export default {
    components: {
      ScrollPanel,
      Tabs,
      Tab,
      Primary,
      Secondary,
      InteractiveSimpleButton,
    },
    props: {
      workbenchSlottedValue: {
        type: Object,
        default: undefined,
      },
      viewOnly: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        moreIcon: MoreIcons,
        insightsUpdated: false,
        insightsHeaderText: 'Primary Insights',
        useMockData: this.$store.getters['workbench/xandrUseMockData'],
        audienceExpansionChanged: false,
        // Local variables that will be updated by watchers
        audienceExpansionEnabled: false,
        allowAudienceExpansionToggle: false,
      }
    },
    mounted() {
      // Initialize local variables from mapped getters
      this.audienceExpansionEnabled = this.audienceExpansionValue
      this.allowAudienceExpansionToggle = this.allowToggle

      let isDuplicateTargetAction = this.$store.getters['workbench/targetCreateAction']
      if (isDuplicateTargetAction) {
        this.configureAudienceExpansionForDuplicate()
        this.updateInsights()
      }
      const unWatch = this.$watch(
        // watch only once until newWorkbenchTarget is available
        () => this.newWorkbenchTarget,
        wbTarget => {
          if (wbTarget) {
            this.audienceExpansionChanged = false
            if (wbTarget.xandr_data && wbTarget.xandr_data.audienceExpansion != null) {
              this.audienceExpansionEnabled = wbTarget.xandr_data.audienceExpansion
              this.$store.dispatch('workbench/' + SET_AUDIENCE_EXPANSION, this.audienceExpansionEnabled)
            }

            if (wbTarget.activation_status === ACTIVATION_STATUS.DRAFT) {
              this.useMockData = this.$store.getters['workbench/xandrUseMockData']
              this.setInsightsHeaderText(this.useMockData)
            } else if (
              wbTarget.xandr_data &&
              wbTarget.xandr_data.query &&
              wbTarget.xandr_data.query.counts.isUsingMockData
            ) {
              // Existing Insights is using mock data
              this.setInsightsHeaderText(true)
            } else {
              this.setInsightsHeaderText(false)
            }
            unWatch()
          }
        },
      )
    },
    computed: {
      ...mapGetters({
        isUsingMockData: 'workbench/xandrUseMockData',
        audienceExpansionValue: 'workbench/audienceExpansion',
        allowToggle: 'workbench/allowAudienceExpansionToggle'
      }),
      audienceExpansion: {
        get() {
          return this.audienceExpansionEnabled
        },
        set() {
          // This will be handled by the onAudienceExpansionToggle method
        },
      },
      disableUpdateInsights() {
        // If audience expansion has been changed, enable the button immediately
        if (this.audienceExpansionChanged) {
          return false
        }

        let td = this.$store.getters['workbench/newTargetDefinition']
        return (
          (!this.insightsUpdated && this.isDraft && td.length > 0
            ? false
            : (td && td.length === 0) || !this.$store.getters['workbench/xandrUpdateInsightsEnabled']) ||
          this.viewOnly ||
          ((this.isLive || (this.isFailed && !this.$store.getters['workbench/xandrUpdateInsightsEnabled'])) &&
            !this.isDigitalSocialDestination)
        )
      },
      buttonText() {
        return 'Update Insights'
      },
      newWorkbenchTarget() {
        return this.$store.getters['workbench/newWorkbenchTarget']
      },
      loadingInsights() {
        return this.$store.getters['workbench/xandrLoadingInsights']
      },
      loadingWorkbench() {
        return this.$store.state.workbench.loadingWorkbench
      },
      isLive() {
        return (this.$store.getters['workbench/newWorkbenchTarget'] || {}).activation_status === ACTIVATION_STATUS.LIVE
      },
      isFailed() {
        return (
          (this.$store.getters['workbench/newWorkbenchTarget'] || {}).activation_status === ACTIVATION_STATUS.FAILED
        )
      },
      isDraft() {
        return (this.$store.getters['workbench/newWorkbenchTarget'] || {}).activation_status === ACTIVATION_STATUS.DRAFT
      },
      isDigitalSocialDestination() {
        return ((this.$store.getters['workbench/newWorkbenchTarget'] || {}).destinations || []).some(destination =>
          [DESTINATIONS.KRUX, DESTINATIONS.LIVERAMP].includes(destination),
        )
      },
    },
    methods: {
      updateInsights() {
        this.insightsUpdated = true
        // Update store state with current toggle value
        this.$store.dispatch('workbench/' + SET_AUDIENCE_EXPANSION, this.audienceExpansionEnabled)
        this.$store.dispatch('workbench/' + QUERY_FOR_DATA)

        // Reset change tracking after update
        this.audienceExpansionChanged = false
      },
      setInsightsHeaderText(mockData = true) {
        this.insightsHeaderText = mockData ? 'Primary Insights (Using Mock Data)' : 'Primary Insights'
      },
      onAudienceExpansionToggle(value) {
        this.audienceExpansionEnabled = value
        // Simply set changed flag to true when toggle is changed
        this.audienceExpansionChanged = true
      },
      configureAudienceExpansionForDuplicate() {
        // Configure audience expansion for duplicated target
        let targetDefinition = this.$store.getters['workbench/newTargetDefinition']
        this.$store.dispatch('workbench/' + CONFIGURE_AUDIENCE_EXPANSION, targetDefinition)
        this.audienceExpansionEnabled = this.audienceExpansionValue
        this.allowAudienceExpansionToggle = this.allowToggle
      },
    },
    watch: {
      isUsingMockData() {
        this.insightsHeaderText = this.isUsingMockData ? 'Primary Insights (Using Mock Data)' : 'Primary Insights'
      },
      // Watch mapped getter values and update local variables
      audienceExpansionValue(newValue) {
        this.audienceExpansionEnabled = newValue
      },
      allowToggle(newValue) {
        this.allowAudienceExpansionToggle = newValue
      },
    },
  }
</script>

<style lang="scss" scoped>
  $slateGray: #435261;
  $lightGrayBackground: #e7ebed;
  $white: white;
  $blue: #0052e6;

  .insights {
    position: relative;
    height: 100%;

    &__headerRow {
      display: flex;
      flex-flow: row nowrap;
      width: 91%;
      justify-content: space-between;
      align-items: center;
      margin-right: 1.2em;
      // margin-bottom: 1em;
      &__tabName {
        text-transform: uppercase;
        font-weight: bold;
        color: $blue;
      }
      &__audienceExpansion {
        display: flex;
        align-items: center;
        margin-left: auto;
        margin-right: 1em;

        ::v-deep .v-input--switch {
          margin-top: 0;
          padding-top: 0;
        }

        .audience-expansion-label {
          font-size: 18px;
          color: darken($slateGray, 15%);
          transition: all 0.2s ease;
          font-weight: 400;

          &--active {
            color: darken($slateGray, 30%);
            font-size: 18px;
          }
        }
      }
      &__moreIcon {
        cursor: pointer;
        .icon {
          height: 7px;
        }
      }
    }

    &__tabular-area {
      margin-bottom: 50px;
      margin-top: 10px;
    }

    &__tabs {
      display: flex;
      flex-flow: column;
      align-items: center;
      height: 100%;
      &__insightButtonRow {
        position: absolute;
        bottom: 0;
        right: 3em;
      }

      .tabs {
        &__list {
          justify-content: flex-start !important;
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// Insights.vue?386ba6b0